import React, { useState, useMemo } from 'react';
import { Copy, Checkmark } from '@core/icons-react';
import { CodeViewer } from '../../../code/CodeViewer';
import { Tag, seg } from '../../../tags/Tag';
import { useCopyToClipboard } from '../../../shared/hooks/useCopyToClipboard';
import { BaseTile, TileHeader, TileFooter } from '../../shared';
import type { 
  CodeTileProps, 
  CodeTileContent, 
  CodeTileFooter 
} from './types';

export function CodeTile({
  title,
  headerAction,
  hideHeader = false,
  tabs,
  content,
  footer,
  hideFooter = false,
  maxWidth = '46.875rem',
  className = '',
  style,
  borderColor = 'var(--tile-border)',
  dividerColor = 'var(--tile-divider)',
  backgroundColor = 'var(--core-layer-01)',
  emptyStateMessage = 'No content available',
  // Simplified mode props
  code,
  language,
  showCopyButton = false,
  showLineNumbers,
  rounded = false
}: CodeTileProps) {
  const { isCopied, copy } = useCopyToClipboard();

  // If using simplified mode (code prop provided), convert to content object
  const normalizedContent: CodeTileContent | ((activeTab?: string) => CodeTileContent) | undefined = useMemo(() => {
    if (code !== undefined) {
      return {
        code,
        language: language || 'text',
        showLineNumbers: showLineNumbers !== undefined ? showLineNumbers : false
      };
    }
    return content;
  }, [code, language, showLineNumbers, content]);
  
  // State for active tab if tabs are enabled
  const [activeTab, setActiveTab] = useState(
    tabs?.activeTab || tabs?.items?.[0]?.id || ''
  );
  
  // Get current content based on active tab
  const currentContent = useMemo(() => {
    if (typeof normalizedContent === 'function') {
      return normalizedContent(activeTab);
    }
    return normalizedContent;
  }, [normalizedContent, activeTab]);
  
  // Get current footer based on active tab
  const currentFooter = useMemo(() => {
    if (typeof footer === 'function') {
      return footer(activeTab);
    }
    return footer;
  }, [footer, activeTab]);
  
  // Handle tab change
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    tabs?.onTabChange?.(tabId);
  };
  
  // Get active tab data
  const activeTabData = tabs?.items?.find(tab => tab.id === activeTab);
  
  // Calculate uniform tab width if needed
  const uniformTabWidth = useMemo(() => {
    if (!tabs?.uniformWidth || !tabs?.items) return undefined;
    
    const longestLabel = tabs.items.reduce((longest, tab) => 
      tab.label.length > longest.length ? tab.label : longest, ''
    );
    
    const charCount = longestLabel.length;
    const boldCharWidth = 0.5;
    const baseWidth = charCount * boldCharWidth;
    const totalWidth = baseWidth + 2; // Add 2rem for padding (1rem each side)
    
    return `${Math.max(totalWidth, 4)}rem`; // Increased min width to 4rem
  }, [tabs]);
  
  // Check if content is available
  if (!normalizedContent) {
    return null;
  }
  
  // Empty state check
  if (!currentContent.code && !emptyStateMessage) {
    return null;
  }
  
  // Empty state rendering
  if (!currentContent.code) {
    return (
      <BaseTile
        className={`core-code-tile core-code-tile--empty ${className}`}
        style={{ maxWidth, padding: '2rem', textAlign: 'center', ...style }}
        rounded={rounded}
      >
        <p style={{
          color: 'var(--core-text-secondary)',
          fontSize: '0.875rem',
          margin: 0
        }}>
          {emptyStateMessage}
        </p>
      </BaseTile>
    );
  }
  
  return (
    <>
      <style>
        {`
          .core-code-tile-tabs::-webkit-scrollbar {
            height: 6px;
          }
          .core-code-tile-tabs::-webkit-scrollbar-track {
            background: transparent;
          }
          .core-code-tile-tabs::-webkit-scrollbar-thumb {
            background: var(--core-border-subtle);
            border-radius: 3px;
          }
          .core-code-tile-tabs::-webkit-scrollbar-thumb:hover {
            background: var(--core-border-strong);
          }
        `}
      </style>
      
      <BaseTile
        className={`core-code-tile ${className}`}
        style={{ maxWidth, overflow: 'visible', position: 'relative', zIndex: 'auto', ...style }}
        rounded={rounded}
      >
        {/* Header */}
        {!hideHeader && (
          <div className="core-code-tile__header" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${dividerColor}`,
            background: backgroundColor,
            padding: '0.625rem 1rem',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            position: 'relative',
            zIndex: 100
          }}>
            {/* Title */}
            {title && (
              <h3 style={{
                margin: 0,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--core-text-primary)'
              }}>
                {title}
              </h3>
            )}
            
            {/* Header Action */}
            {headerAction?.type === 'dropdown' && (
              <Tag
                template="action-sm"
                segments={[seg.text(headerAction.selectedValue || '', { size: 'md' })]}
                dropdown="option"
                dropdownOptions={headerAction.options?.map((opt: string) => ({
                  id: opt,
                  value: opt,
                  label: opt
                })) || []}
                selectedItem={headerAction.selectedValue}
                onSelectionChange={headerAction.onSelectionChange}
                dropdownPosition={headerAction.dropdownPosition || 'right'}
                dropdownRequireSelection
              />
            )}
            
            {headerAction?.type === 'button' && (
              <Tag
                template="action-sm"
                segments={[seg.text(headerAction.label || '', { size: 'md' })]}
                onClick={headerAction.onClick}
              >
                {headerAction.icon}
              </Tag>
            )}
          </div>
        )}
        
        {/* Tabs (if enabled) */}
        {tabs?.enabled && tabs?.items?.length > 0 && (
          <div className="core-code-tile__tabs-container" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${dividerColor}`,
            background: backgroundColor
          }}>
            <div 
              className="core-code-tile-tabs"
              style={{
                display: 'flex',
                alignItems: 'stretch',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--core-border-subtle) transparent',
                WebkitOverflowScrolling: 'touch',
                flex: '1 1 auto',
                minWidth: 0,
                maxWidth: '100%',
                maxHeight: '48px',
                position: 'relative'
              }}
            >
              {tabs.items.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`core-code-tile__tab ${
                    activeTab === tab.id ? 'core-code-tile__tab--active' : ''
                  }`}
                  style={{
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'transparent',
                    color: activeTab === tab.id
                      ? 'var(--core-text-primary)'
                      : 'var(--core-text-secondary)',
                    fontSize: '0.8125rem',
                    fontWeight: activeTab === tab.id ? 500 : 400,
                    cursor: 'pointer',
                    borderBottom: activeTab === tab.id 
                      ? '2px solid var(--core-border-strong)' 
                      : '2px solid transparent',
                    transition: 'color 0.15s ease, border-bottom 0.15s ease, background-color 0.15s ease',
                    marginBottom: '-1px',
                    width: uniformTabWidth,
                    minWidth: uniformTabWidth || '4rem', // Ensure minimum width
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    flexGrow: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.color = 'var(--core-text-primary)';
                      e.currentTarget.style.background = 'var(--core-layer-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.color = 'var(--core-text-secondary)';
                      e.currentTarget.style.background = 'transparent';
                    } else {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <span style={{ position: 'relative', display: 'inline-block' }}>
                    {/* Invisible spacer with bold weight to reserve width */}
                    <span 
                      aria-hidden="true"
                      style={{
                        display: 'inline-block',
                        fontWeight: 500, // Always bold to reserve max width
                        fontSize: '0.8125rem',
                        visibility: 'hidden',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        userSelect: 'none'
                      }}
                    >
                      {tab.label}
                    </span>
                    {/* Visible text that changes weight */}
                    <span 
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {tab.label}
                    </span>
                  </span>
                </button>
              ))}
            </div>
            
            {/* Empty right side for consistency */}
            <div style={{ padding: '0.5rem', flexShrink: 0 }} />
          </div>
        )}
        
        {/* Content */}
        <div className="core-code-tile__content" style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
          background: backgroundColor,
          position: 'relative'
        }}>
          {/* Code Viewer */}
          <CodeViewer
            code={currentContent.code}
            language={currentContent.language || 'JavaScript'}
            showLineNumbers={currentContent.showLineNumbers}
          />
          
          {/* Copy Button - positioned in top-right */}
          {showCopyButton && (
            <div style={{
              position: 'absolute',
              top: '0.75rem',
              right: '0.75rem',
              zIndex: 10
            }}>
              <Tag
                template="action-sm"
                segments={[seg.icon(isCopied ? Checkmark : Copy, { size: 14 })]}
                onClick={() => copy(currentContent.code, 'Code snippet')}
              />
            </div>
          )}
          
          {/* Footer (if present and not hidden) */}
          {!hideFooter && currentFooter && (
            <>
              {/* Divider */}
              <div style={{
                height: '1px',
                background: borderColor,
                margin: '0'
              }} />
              
              {/* Footer Content */}
              <div className="core-code-tile__footer" style={{
                fontSize: '0.6875rem',
                color: 'var(--core-text-secondary)',
                fontWeight: 500,
                padding: '0.75rem 1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: '2.25rem'
              }}>
                {/* Description */}
                <span className="core-code-tile__footer-description">
                  {currentFooter.description || 
                   (activeTabData?.description || '')}
                </span>
                
                {/* Action */}
                {currentFooter.action?.type === 'tertiary-button' && (
                  <Tag
                    template="action-sm"
                    segments={[seg.text(currentFooter.action.text || '', { size: 'md' })]}
                    active={currentFooter.action.active}
                    onClick={currentFooter.action.onClick}
                  >
                    {currentFooter.action.icon}
                  </Tag>
                )}
                
                {currentFooter.action?.type === 'custom' && (
                  currentFooter.action.component
                )}
              </div>
            </>
          )}
        </div>
      </BaseTile>
    </>
  );
}